/* eslint-disable react/no-unused-state */
import React, { Component } from 'react';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl-next';
import PropTypes from 'prop-types';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import AkComment, { CommentAuthor } from '@atlaskit/comment';
import AkLozenge from '@atlaskit/lozenge';
import { B50, N200 } from '@atlaskit/theme/colors';

import { ProfileAvatar, ProfileLink } from '@confluence/profile';
import { CommentRenderer } from '@confluence/comment';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui';
import { SitePermissionType } from '@confluence/render-for-feature-flag';
import { CopyLinkButton } from '@confluence/inline-comments-common';
import { withRoutesContext } from '@confluence/route-manager';
import { A11Y_CONTENT_SUFFIX } from '@confluence/comments-util/entry-points/commentsConstants';

import CommentActions from './CommentActions';

const LEVEL_MARGIN = 40;

const i18n = defineMessages({
	/**
	 * The message below is duplicated in these files:
	 * next/packages/resolved-inline-comments-dialog/src/ResolvedInlineCommentsDialogComponent.tsx
	 * next/packages/inline-comments/src/i18n.ts
	 *
	 * Please make sure that any changes are reflected in the aforementioned files.
	 */
	a11yCommentLabel: {
		id: 'comment.a11y.comment.wrapper.label',
		defaultMessage: 'Comment by {username}',
		description: 'Label that announced by screen reader when user select comment',
	},

	authorLozenge: {
		id: 'comment.author-lozenge',
		defaultMessage: 'Author',
		description:
			'Text for a lozenge showing if a user who commented on a blog post is the author of that blog post',
	},
});

/**
 * right-shadow gets added by atlaskit when we have horizontal scroll with code block
 * Adding z-index(7) lower than z-index(8) for sticky Editor
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CommentContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginTop: '10px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) => (props.isFocused ? token('color.background.selected', B50) : 'inherit'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: (props) => (props.isFocused ? '9px 0 9px 10px' : 0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: (props) =>
		props.isFocused
			? `-9px 0px 13px ${props.level * LEVEL_MARGIN - 10}px`
			: `0px 0px 24px ${props.level * LEVEL_MARGIN}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.right-shadow': {
		zIndex: 7,
	},
	outline: 'none',
});
CommentContainer.displayName = 'CommentContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CommentContentContainer = styled.div({
	marginTop: token('space.negative.050', '-4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarContainer = styled.div({
	width: '32px',
	height: '32px',
	marginRight: '10px',
	float: 'left',
});
AvatarContainer.displayName = 'AvatarContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionContainer = styled.div({
	marginLeft: '50px',
	marginTop: token('space.negative.150', '-12px'),
});
ActionContainer.displayName = 'ActionContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AuthorContainer = styled.div({
	display: 'flex',
	alignItems: 'flex-start',
	flexDirection: 'column',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DateLinkContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DateContainer = styled.span({
	color: token('color.text.subtle', N200),
	fontSize: '12px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AuthorNameContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	fontSize: '16px',
	lineHeight: 1.428,
	fontWeight: 700,
});

AuthorContainer.displayName = 'AuthorContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.span({
	marginLeft: token('space.100', '8px'),
	verticalAlign: 'top',
	display: 'flex',
});

export default withRoutesContext(
	injectIntl(
		class PageCommentRenderer extends Component {
			static propTypes = {
				createAnalyticsEvent: PropTypes.func,
				contentId: PropTypes.string,
				contentType: PropTypes.oneOf(['blogpost', 'page']),
				spaceId: PropTypes.string.isRequired,
				comment: PropTypes.object.isRequired,
				commentActions: PropTypes.object.isRequired,
				canReplyToComment: PropTypes.bool.isRequired,
				canViewProfile: PropTypes.bool,
				isFocused: PropTypes.bool,
				showAuthorLabel: PropTypes.bool,
				intl: PropTypes.object,
				shouldAutofocus: PropTypes.bool,
				onAutoFocused: PropTypes.func,
				onRendered: PropTypes.func,
				currentUserAccountId: PropTypes.string.isRequired,
			};

			static defaultProps = {
				canViewProfile: false,
				isFocused: false,
				showAuthorLabel: false,
			};

			state = {
				_root: undefined,
				isFallbackContent: false,
				showingCopyButton: false,
			};

			componentDidUpdate() {
				if (this.props.shouldAutofocus) {
					this.wrapperRef.current && this.wrapperRef.current.focus();
					this.props.onAutoFocused && this.props.onAutoFocused();
				}
			}

			wrapperRef = React.createRef();

			checkForFallbackContent = () => {
				const { isFallbackContent } = this.state;

				if (!isFallbackContent) {
					this.setState({ isFallbackContent: true });
				}
			};

			getCommentAuthorProps = () => {
				const {
					comment: { createdBy, createdByAuthor },
					showAuthorLabel,
				} = this.props;

				if (!createdBy) {
					return {
						displayName: 'Anonymous',
						isAnonymous: true,
					};
				} else {
					return {
						userId: createdBy.accountId,
						displayName: createdBy.displayName,
						src: createdBy.avatarUrl,
						isAnonymous: !createdBy.accountId,
						permissionType: createdBy.permissionType,
						createdByAuthor,
						showAuthorLabel,
					};
				}
			};

			renderAuthor() {
				/**
				 * Note: we are rendering the "author" and "guest" lozenge here as atlaskit's comment currently does not
				 * support multiple lozenges or custom lozenge styling
				 */
				const {
					userId,
					displayName,
					isAnonymous,
					permissionType,
					createdByAuthor,
					showAuthorLabel,
				} = this.getCommentAuthorProps();

				const { comment } = this.props;
				const { origin } = window.location;

				return (
					<AuthorContainer data-cy="comment-author" data-testid="author-container">
						<AuthorNameContainer>
							<ProfileLink
								userId={userId}
								fullName={displayName}
								isAnonymous={isAnonymous}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								style={{ wordBreak: 'break-word', textDecorationColor: token('color.link', N200) }}
							>
								<CommentAuthor>{isAnonymous ? 'Anonymous' : displayName}</CommentAuthor>
							</ProfileLink>
							{createdByAuthor && showAuthorLabel ? (
								<LozengeWrapper>
									<AkLozenge>
										<FormattedMessage {...i18n.authorLozenge} />
									</AkLozenge>
								</LozengeWrapper>
							) : null}
							{permissionType === SitePermissionType.EXTERNAL && (
								<LozengeWrapper>
									<ExternalCollaboratorUserLozenge />
								</LozengeWrapper>
							)}
						</AuthorNameContainer>

						<DateLinkContainer>
							<DateContainer data-testid="date-container">{comment.createdFriendly}</DateContainer>
							<CopyLinkButton
								visible={this.state.showingCopyButton}
								textToCopy={`${origin}/wiki${this.props.comment.linkWebui}`}
							/>
						</DateLinkContainer>
					</AuthorContainer>
				);
			}

			renderAvatar() {
				const { canViewProfile } = this.props;

				const { userId, src, isAnonymous, displayName } = this.getCommentAuthorProps();

				return (
					<AvatarContainer>
						<ProfileAvatar
							canViewProfile={canViewProfile}
							userId={userId}
							src={src}
							isAnonymous={isAnonymous}
							name={displayName}
						/>
					</AvatarContainer>
				);
			}

			_setRoot = (ref) => this.setState({ _root: ref });

			handleMouseEventOnWrapper = (eventType) => () => {
				this.setState({ showingCopyButton: eventType === 'enter' });
			};

			renderCommentContent() {
				const {
					comment: { id, adf },
					onRendered,
				} = this.props;

				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					<CommentContentContainer className="wiki-content" id={`${id}-${A11Y_CONTENT_SUFFIX}`}>
						<CommentRenderer
							commentId={id}
							adf={adf}
							onContentFallback={this.checkForFallbackContent}
							type="pageComment"
							onRendered={onRendered}
						/>
					</CommentContentContainer>
				);
			}

			render() {
				const {
					comment,
					isFocused,
					commentActions,
					canReplyToComment,
					contentId,
					contentType,
					intl: { formatMessage },
					spaceId,
				} = this.props;

				const { isFallbackContent, showingCopyButton } = this.state;

				const { deleteComment, editComment, replyToComment } = commentActions;

				const wrapperLabel = formatMessage(i18n.a11yCommentLabel, {
					username: this.getCommentAuthorProps().displayName,
				});

				// FIXME: Elements with ARIA roles must use a valid, non-abstract ARIA role
				/* eslint-disable jsx-a11y/aria-role */
				return (
					<CommentContainer
						key={comment.id}
						id={`comment-${comment.id ? comment.id : 'new'}`}
						data-testid={`comment-${comment.id ? comment.id : 'new'}`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className="fabric-comment"
						isFocused={isFocused}
						level={comment.ancestors ? comment.ancestors.length : 0}
						role="comment"
						aria-label={wrapperLabel}
						ref={this.wrapperRef}
						tabIndex={-1}
						onMouseEnter={this.handleMouseEventOnWrapper('enter')}
						onMouseLeave={this.handleMouseEventOnWrapper('leave')}
					>
						<AkComment
							author={this.renderAuthor()}
							avatar={this.renderAvatar()}
							content={this.renderCommentContent()}
						/>
						{commentActions && (
							<ActionContainer>
								<CommentActions
									comment={comment}
									contentId={contentId}
									contentType={contentType}
									spaceId={spaceId}
									deleteComment={deleteComment}
									editComment={editComment}
									replyToComment={replyToComment}
									canReplyToComment={canReplyToComment}
									restrictEdit={isFallbackContent}
									showCopyLinkButton={showingCopyButton}
									createAnalyticsEvent={this.props.createAnalyticsEvent}
									currentUserAccountId={this.props.currentUserAccountId}
								/>
							</ActionContainer>
						)}
					</CommentContainer>
				);
			}
		},
	),
);
